import { db } from "@/lib/firebase";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { CaseModel } from "../types";
import { imageUrlService } from "./imageUrlService";

export const templateService = {
  /**
   * Lấy danh sách sản phẩm mẫu từ Firestore
   */
  getTemplates: async (productType: 'phone_case' | 'coffee_cup'): Promise<CaseModel[]> => {
    try {
      const templatesRef = collection(db, "product_templates");
      const q = query(
        templatesRef,
        where("product_type", "==", productType),
        where("is_active", "==", true),
        orderBy("display_order", "asc")
      );

      const querySnapshot = await getDocs(q);
      const templates: CaseModel[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();

        // Calculate cache buster from updated_at
        let cacheBuster = '';
        if (data.updated_at) {
           const seconds = data.updated_at.seconds || (new Date(data.updated_at).getTime() / 1000);
           cacheBuster = Math.floor(seconds).toString();
        }

        const addCacheBuster = (url: string | undefined) => {
             if (!url || !cacheBuster) return url;
             const separator = url.includes('?') ? '&' : '?';
             return `${url}${separator}v=${cacheBuster}`;
        };

        // 🚀 PRIORITY: Use CDN URLs (Cloudflare R2 - zero egress fees)
        // Fallback to Modal URLs if CDN not available (for old templates)
        let imageUrl = data.cdn_thumbnail_url || data.cdn_url ||
          imageUrlService.getTemplateImageUrl(data.image_path, data.thumbnail_path, data.updated_at);

        // Add cache buster to ALL URLs
        if (imageUrl) {
            imageUrl = addCacheBuster(imageUrl);
        }

        if (!data.cdn_url && !data.cdn_thumbnail_url) {
            console.warn(`⚠️ Template ${doc.id} uses Modal URL (no CDN). Run migration script to update to Cloudflare R2.`);
        }

        // Resolve clean and mask URLs from CDN, with fallback to Modal URLs
        // Prefer thumbnail (faster loading)
        let cleanUrl = data.cdn_clean_thumbnail_url || data.cdn_clean_url ||
          (data.clean_image_path
            ? imageUrlService.getTemplateImageUrl(data.clean_image_path, data.clean_thumbnail_path, data.updated_at)
            : undefined);

        // Add cache buster to clean URL if it exists
        if (cleanUrl) {
            cleanUrl = addCacheBuster(cleanUrl);
        }

        // Mask URL from CDN or Modal fallback
        let maskUrl = data.cdn_mask_url ||
          (data.mask_image_path
            ? imageUrlService.getTemplateImageUrl(data.mask_image_path, undefined, data.updated_at)
            : undefined);

        // Add cache buster to mask URL if it exists
        if (maskUrl) {
            maskUrl = addCacheBuster(maskUrl);
        }

        // FALLBACK: If no mask image available, generate default mask on frontend
        // This ensures all templates can be used even if mask image is missing
        if (!maskUrl) {
            console.warn(`⚠️ Template ${doc.id} has no CDN mask image. Frontend will generate fallback mask.`);
            // Use a special marker so frontend can detect and generate fallback
            maskUrl = 'GENERATE_FALLBACK_MASK';
        }

        templates.push({
          id: doc.id,
          name: data.name,
          url: imageUrl, // CDN URL only (from Cloudflare R2)
          cleanUrl,  // Clean image without marks (from CDN)
          maskUrl,   // Mask image with red boxes (from CDN or fallback marker)
          category: data.category,
          haravan_variant_id: data.haravan_variant_id,  // Haravan integration
          haravan_product_id: data.haravan_product_id   // Haravan integration
        });
      });

      return templates;
    } catch (error) {
      console.error("Error fetching templates:", error);
      return []; // Trả về mảng rỗng nếu chưa có dữ liệu hoặc lỗi
    }
  }
};
